import { useMemo } from 'react'
import { Link as RouterLink, useParams, useLocation } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import _ from 'lodash'

function PageBreadcrumbs({ entities = {} }) {
  const params = useParams()
  const location = useLocation()

  const breadcrumbs = useMemo(() => {
    const items = []
    const { teamSlug, nodeId, projectSlug, serviceSlug } = params
    const pathSegments = location.pathname.split('/').filter(Boolean)

    // Always start with Teams (home)
    items.push({
      key: 'home',
      labelId: 'breadcrumbs.home',
      to: '/',
      isLast: pathSegments.length === 0
    })

    // Handle create team page (only /teams/new)
    if (pathSegments[0] === 'teams' && pathSegments[1] === 'new') {
      items.push({
        key: 'createTeam',
        labelId: 'breadcrumbs.createTeam',
        to: null,
        isLast: true
      })
      return items
    }

    // Team level
    if (teamSlug) {
      const team = entities.team
      const teamName = _.get(team, 'name', teamSlug)
      const teamSlugForUrl = _.get(team, 'slug', teamSlug)
      const isTeamLast = !nodeId && !projectSlug && !pathSegments.includes('nodes') && !pathSegments.includes('docker')

      items.push({
        key: 'team',
        text: teamName,
        to: `/teams/${teamSlugForUrl}`,
        isLast: isTeamLast
      })

      // Nodes branch
      if (pathSegments.includes('nodes')) {
        items.push({
          key: 'nodes',
          labelId: 'breadcrumbs.nodes',
          to: `/teams/${teamSlugForUrl}/nodes`,
          isLast: !nodeId
        })

        if (nodeId) {
          const nodeName = _.get(entities, 'node.name', nodeId)
          items.push({
            key: 'node',
            text: nodeName,
            to: null,
            isLast: true
          })
        }
      }

      // Docker/Registries branch
      if (pathSegments.includes('docker') && pathSegments.includes('registries')) {
        items.push({
          key: 'registries',
          labelId: 'breadcrumbs.dockerRegistries',
          to: `/teams/${teamSlugForUrl}/docker/registries`,
          isLast: true
        })
      }

      // Docker/Projects branch
      if (pathSegments.includes('docker') && !pathSegments.includes('registries')) {
        items.push({
          key: 'projects',
          labelId: 'breadcrumbs.dockerProjects',
          to: `/teams/${teamSlugForUrl}/docker/projects`,
          isLast: !projectSlug
        })

        if (projectSlug) {
          const project = entities.project
          const projectName = _.get(project, 'name', projectSlug)
          const projectSlugForUrl = _.get(project, 'slug', projectSlug)
          const isServiceCreatePage = pathSegments.includes('services') && pathSegments.includes('new') && !serviceSlug

          items.push({
            key: 'project',
            text: projectName,
            to: `/teams/${teamSlugForUrl}/docker/projects/${projectSlugForUrl}`,
            isLast: false
          })

          // Detect which tab is active
          const tabLabelMap = {
            'services': 'breadcrumbs.services',
            'packages': 'breadcrumbs.packages',
            'ingress-controllers': 'breadcrumbs.ingressControllers',
            'deployments': 'breadcrumbs.deployments',
            'yaml': 'breadcrumbs.yaml',
            'networks': 'breadcrumbs.networks'
          }

          // Determine active tab from path
          let activeTab = 'services' // default
          if (pathSegments.includes('packages')) activeTab = 'packages'
          else if (pathSegments.includes('ingress-controllers')) activeTab = 'ingress-controllers'
          else if (pathSegments.includes('deployments')) activeTab = 'deployments'
          else if (pathSegments.includes('yaml')) activeTab = 'yaml'
          else if (pathSegments.includes('networks')) activeTab = 'networks'
          else if (pathSegments.includes('services')) activeTab = 'services'

          // Check if we're on a sub-page (edit, new, imports) within the tab
          const isTabSubPage = serviceSlug || isServiceCreatePage ||
            pathSegments.includes('edit') || pathSegments.includes('new') ||
            pathSegments.includes('imports')

          const tabPath = activeTab === 'services'
            ? `/teams/${teamSlugForUrl}/docker/projects/${projectSlugForUrl}`
            : `/teams/${teamSlugForUrl}/docker/projects/${projectSlugForUrl}/${activeTab}`

          items.push({
            key: 'tab',
            labelId: tabLabelMap[activeTab],
            to: isTabSubPage ? tabPath : null,
            isLast: !isTabSubPage && !serviceSlug
          })

          // Service create page
          if (isServiceCreatePage) {
            items.push({
              key: 'createService',
              labelId: 'breadcrumbs.createService',
              to: null,
              isLast: true
            })
          }

          // Package import edit page
          if (activeTab === 'packages' && pathSegments.includes('edit')) {
            items.push({
              key: 'editPackageImport',
              labelId: 'breadcrumbs.editPackageImport',
              to: null,
              isLast: true
            })
          }

          // Package import create page
          if (activeTab === 'packages' && pathSegments.includes('new')) {
            items.push({
              key: 'importPackage',
              labelId: 'breadcrumbs.importPackage',
              to: null,
              isLast: true
            })
          }

          // Service detail
          if (serviceSlug) {
            const service = entities.service
            const serviceName = _.get(service, 'name', serviceSlug)
            const serviceSlugForUrl = _.get(service, 'slug', serviceSlug)

            items.push({
              key: 'service',
              text: serviceName,
              to: `/teams/${teamSlugForUrl}/docker/projects/${projectSlugForUrl}/services/${serviceSlugForUrl}`,
              isLast: true
            })
          }
        }
      }
    }

    return items
  }, [params, location.pathname, entities])

  return (
    <Breadcrumbs>
      {breadcrumbs.map((item) => {
        const content = item.text
          ? item.text
          : <FormattedMessage id={item.labelId} />

        if (item.isLast || !item.to) {
          return (
            <Typography key={item.key} color="text.primary">
              {content}
            </Typography>
          )
        }

        return (
          <Link
            key={item.key}
            component={RouterLink}
            to={item.to}
            underline="hover"
            color="inherit"
          >
            {content}
          </Link>
        )
      })}
    </Breadcrumbs>
  )
}

export default PageBreadcrumbs
